import React, { useContext, useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import Loader from './Loader';
import { db } from './firebase-config';
import { useUser } from '../context/UserProvider';
import { DataContext, Prize } from '../context/DataContext';
import { useAppConfig } from '../context/AppConfigContext';
import { APP_DEFAULTS } from '../config';
import { hasReachedMaxSpins, isCampaignEnded } from '../utils/campaign';
import {
  getAccountLookupErrorMessage,
  isAccountNotFoundError,
  lookupAccountByPhone,
  normalizeMoroccanPhoneNumber,
} from '../services/accountLookup';

const SubscriptionForm: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const navigate = useNavigate();
  const { setUser } = useUser();
  const { setData } = useContext(DataContext);
  const { config } = useAppConfig();

  const loaderDelay = config.loaderDelay ?? APP_DEFAULTS.loaderDelay;
  const campaignIsEnded = isCampaignEnded(config.campaignEndsAt);
  const accountRequiredMessage =
    config.accountRequiredMessage || APP_DEFAULTS.accountRequiredMessage;

  const campaignBlockMessage = useMemo(() => {
    if (campaignIsEnded) {
      return 'This campaign has already ended.';
    }

    if (!config.campaignEnabled) {
      return 'This campaign is currently disabled.';
    }

    return null;
  }, [campaignIsEnded, config.campaignEnabled]);

  const activeMessage = errorMsg || campaignBlockMessage;
  const isAccountRequiredNotice = activeMessage === accountRequiredMessage;

  useEffect(() => {
    const fetchPrizes = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, APP_DEFAULTS.firestoreCollection));
        const dataList = querySnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        })) as Prize[];
        setData(dataList);
      } catch (error) {
        console.error('Error fetching prizes:', error);
        setErrorMsg('Failed to load prizes. Please try again.');
      }
    };

    fetchPrizes();
  }, [setData]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (campaignBlockMessage) {
      setErrorMsg(campaignBlockMessage);
      return;
    }

    const normalizedPhoneNumber = normalizeMoroccanPhoneNumber(phoneNumber);

    if (!normalizedPhoneNumber) {
      setErrorMsg('Veuillez saisir un numero marocain valide (0, 212 ou +212, puis 6 ou 7 et 8 chiffres).');
      return;
    }

    if (hasReachedMaxSpins(normalizedPhoneNumber, config.maxSpinsPerUser)) {
      setErrorMsg('This participant has already reached the allowed number of spins.');
      return;
    }

    setLoading(true);
    setErrorMsg(null);

    try {
      const account = await lookupAccountByPhone(normalizedPhoneNumber);

      if (!account.exists) {
        setLoading(false);
        setErrorMsg(accountRequiredMessage);
        return;
      }

      setUser({
        name: account.name || account.normalizedPhoneNumber,
        phone: account.normalizedPhoneNumber,
      });

      setTimeout(() => {
        setLoading(false);
        navigate('/wheel-of-fortune');
      }, loaderDelay);
    } catch (error) {
      console.error('Failed to submit entry:', error);
      setLoading(false);
      if (isAccountNotFoundError(error)) {
        setErrorMsg(accountRequiredMessage);
        return;
      }

      setErrorMsg(getAccountLookupErrorMessage(error));
    }
  };

  const inputClass =
    'mt-1 opacity-50 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-300 focus:border-yellow-500 sm:text-sm';

  return (
    <div className="flex justify-center items-center h-screen min-h-screen bg-black bg-opacity-0 animate-fade animate-duration-[3000ms] animate-ease-out">
      <Loader show={loading} />
      <div className="w-full max-w-md p-6 bg-opacity-100 shadow-md rounded-md">
        {activeMessage && (
          <div
            className={
              isAccountRequiredNotice
                ? 'mb-5 rounded-xl border-2 border-yellow-300 bg-white/95 px-4 py-3 text-center shadow-[0_12px_30px_rgba(0,0,0,0.35)]'
                : 'mb-4 rounded-md border border-red-400/40 bg-red-500/10 px-4 py-3 text-center'
            }
          >
            {isAccountRequiredNotice && (
              <p className="mb-1 text-xs font-bold uppercase tracking-[0.2em] text-slate-900">
                Compte requis
              </p>
            )}
            <p
              className={
                isAccountRequiredNotice
                  ? 'text-sm font-bold leading-6 text-red-700'
                  : 'text-sm text-red-300'
              }
            >
              {activeMessage}
            </p>
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="phone-number" className="block text-gray-300 font-medium text-left">
              Phone Number
            </label>
            <input
              type="tel"
              id="phone-number"
              name="phone"
              className={inputClass}
              required
              inputMode="tel"
              autoComplete="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
          </div>
          <div className="btn-margin">
            <button type="submit" className="contactbutton" disabled={Boolean(campaignBlockMessage)}>
              Continue
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubscriptionForm;
